import { useEffect, useRef, useState } from 'react';
import {
  CanInventoryReturnValidationError,
  InventoryReturnRequest,
  Order,
} from '../../../../../clients/quipli-service/order/order';
import { useOrderApiV1 } from '../../../../../clients/quipli-service/order/order-api-v1';
import { useProductApiV1 } from '../../../../../clients/quipli-service/product/product-api-v1';
import { CalculateOverageChargeResult } from '../../../../../clients/quipli-service/product/product';
import { toast } from '../../../../../util/toasts';
import { safeRound } from '../../../../../util/util';

export type HourOverageOverride = {
  orderProductId: number;
  unitId: number;
  overageCharge: number;
  taxable: boolean;
};

// Reusable hook to calculate hour overages
export function useHourOverageCalculation(
  inventoryReturnRequest: InventoryReturnRequest | null | undefined,
  order: Order | null | undefined,
  checkCanInventoryReturn = false,
  debounceTime = 500,
) {
  const { calculateHourOverageCharge } = useProductApiV1();
  const { canInventoryReturnOrder } = useOrderApiV1();

  const [abortController, setAbortController] = useState<AbortController | null>(null);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [isCalculating, setIsCalculating] = useState<boolean>(false);
  const [canInventoryReturn, setCanInventoryReturn] = useState<CanInventoryReturnValidationError[]>();

  const [hourOverages, setHourOverages] = useState<CalculateOverageChargeResult[]>([]);
  const [hourOverageOverrides, setHourOverageOverrides] = useState<Array<HourOverageOverride>>([]);

  // Cancel ongoing requests and timers on unmount
  useEffect(() => {
    return () => {
      if (abortController) {
        abortController.abort();
      }
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (inventoryReturnRequest && order) {
      const calculateOverageRequest = inventoryReturnRequest.hourReadings.flatMap(({ unitId, hourReading }) => {
        const op = order.products.find(
          (op) =>
            inventoryReturnRequest.orderProductIds.includes(op.id) && op.assignedUnits.some((au) => au.id === unitId),
        );
        if (op && !!hourReading) {
          return [
            {
              orderId: op.orderId,
              orderProductId: op.id,
              unitId,
              start: op.rentalStartDate,
              end: op.rentalEndDate!,
              hoursIn: hourReading,
            },
          ];
        } else {
          return [];
        }
      });

      if (checkCanInventoryReturn) {
        canInventoryReturnOrder(order.id, inventoryReturnRequest)
          .then(setCanInventoryReturn)
          .catch((e) => toast(e.message, 'error'));
      }

      if (calculateOverageRequest.length > 0 && !order.isCycleBilled) {
        // Cancel any pending request
        if (abortController) {
          abortController.abort();
        }

        // Clear any pending timeout
        if (debounceTimeoutRef.current) {
          clearTimeout(debounceTimeoutRef.current);
        }

        // Create new abort controller
        const newAbortController = new AbortController();
        setAbortController(newAbortController);
        setIsCalculating(true);

        // Debounce the request
        debounceTimeoutRef.current = setTimeout(async () => {
          try {
            // Only continue if not aborted during the timeout
            if (!newAbortController.signal.aborted) {
              const overages = await calculateHourOverageCharge(calculateOverageRequest, newAbortController.signal);

              // Double check again in case it was aborted during the API call
              if (!newAbortController.signal.aborted) {
                setHourOverages(overages);
                setHourOverageOverrides(
                  overages.map((ov) => {
                    return {
                      orderProductId: ov.orderProductId,
                      unitId: ov.unitId,
                      overageCharge: safeRound(ov.overageCharge),
                      taxable: order.products.find((op) => op.id === ov.orderProductId)!.product.taxable,
                    };
                  }),
                );
              }
            }
          } catch (e) {
            // Don't show error if it was just cancelled
            if ((e as Error).name !== 'AbortError' && !newAbortController.signal.aborted) {
              console.log(e);
              toast(`Error calculating hour overages`, 'error');
            }
          } finally {
            if (!newAbortController.signal.aborted) {
              setIsCalculating(false);
            }
          }
        }, debounceTime);
      }
    }
  }, [inventoryReturnRequest, order, debounceTime]);

  return {
    canInventoryReturn,
    hourOverages,
    hourOverageOverrides,
    setHourOverageOverrides,
    isCalculating,
  };
}
